import { DynamicTableSVG } from "./shapes/DynamicFurnitureShape/DynamicFurnitureShape";
import { IconFromSet, IconValue } from "@gettactic/components";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { Image } from "react-konva";
import { Furniture } from "../../furniture-list";
import { useMapCanvas } from "./contexts/MapCanvasContext";

export interface IconToolGhostItemProps {
  icon: IconValue;
}

export interface FurnitureToolGhostItemProps {
  furniture: Furniture;
}

export type SvgGhostItemProps =
  | IconToolGhostItemProps
  | FurnitureToolGhostItemProps;

export const SvgGhostItem = React.forwardRef<
  React.ComponentRef<typeof Image>,
  SvgGhostItemProps
>((props, ref) => {
  const [konvaImage, setKonvaImage] = React.useState<HTMLImageElement | null>(
    null
  );
  const { mapIcons } = useMapCanvas();

  const selectedIcon = (props as IconToolGhostItemProps).icon;
  const selectedFurniture = (props as FurnitureToolGhostItemProps).furniture;

  React.useEffect(() => {
    if (!selectedIcon && !selectedFurniture) return;
    setKonvaImage(null);
    const img = new window.Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    img.onload = () => {
      setKonvaImage(img);
    };

    if (selectedIcon) {
      // Check if this is an API-sourced icon (merged into tactic set)
      const apiIcon = mapIcons?.find((i) => i.name === selectedIcon.icon && i.category === "icon");
      if (apiIcon?.icon_url) {
        img.crossOrigin = "anonymous";
        img.src = apiIcon.icon_url;
      } else {
        const svgString = ReactDOMServer.renderToStaticMarkup(
          <IconFromSet icon={selectedIcon} size={64} />
        );
        img.src =
          "data:image/svg+xml;base64," +
          btoa(unescape(encodeURIComponent(svgString)));
      }
      return;
    }

    if (selectedFurniture) {
      img.crossOrigin = "anonymous";
      img.src = selectedFurniture.dynamicTable
        ? "data:image/svg+xml;charset=utf-8,"+encodeURIComponent(ReactDOMServer.renderToStaticMarkup(<DynamicTableSVG layout={selectedFurniture.dynamicTable} />))
        : selectedFurniture.url;
    }
  }, [selectedIcon, selectedFurniture, mapIcons]);

  if (!konvaImage) return null;

  return (
    <Image
      image={konvaImage || undefined}
      width={20}
      height={20}
      scaleX={1}
      scaleY={1}
      x={100}
      y={100}
      ref={ref}
    />
  );
});
